/* eslint-disable react/jsx-props-no-spreading */
import { FC, ComponentProps, FocusEventHandler, ChangeEvent, TextareaHTMLAttributes } from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { allProps, Box, Colors, Input, MultipleLine, Typography } from '@altermaind/web/ui';

type TypographyProps = ComponentProps<typeof Typography>;

type Props = {
  control: Control<FieldValues>;
  name: string;
  containerProps?: ComponentProps<typeof Box>;
  additonalProps?: Record<string, unknown>;
  manipulator?: (value: string) => string;
  textColor?: keyof Colors;
  labelProps?: TypographyProps;
  placeholderProps?: TypographyProps;
} & ComponentProps<typeof Input>;

const StyleWrapper = styled(Box)<{
  $labelProps?: TypographyProps;
  $placeholderProps?: TypographyProps;
}>(
  ({ theme, $labelProps, $placeholderProps }) => css`
    width: 100%;
    & > div > div:first-child {
      ${$labelProps ? allProps({ ...$labelProps, theme }) : ''}
    }
    & textarea::placeholder {
      ${$placeholderProps ? allProps({ ...$placeholderProps, theme }) : ''}
    }
  `,
);

export const WrappedTextArea: FC<Props> = ({
  control,
  name,
  additonalProps,
  containerProps,
  manipulator,
  textColor,
  labelProps,
  placeholderProps,
  color: _color,
  ...props
}) => {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Box {...containerProps}>
      <StyleWrapper $labelProps={labelProps} $placeholderProps={placeholderProps}>
        <Controller
          control={control}
          name={name}
          rules={{ ...additonalProps }}
          render={({ field }) => {
            const { ref, onChange, onBlur, name: fieldName, value } = field;
            return (
              <MultipleLine
                {...(props as TextareaHTMLAttributes<HTMLTextAreaElement>)}
                name={fieldName}
                value={value}
                color={textColor}
                inputRef={ref}
                label={additonalProps?.required && props.label ? `*${props.label}` : props.label}
                onBlur={onBlur as FocusEventHandler<HTMLTextAreaElement>}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                  const manipulatedValue = manipulator
                    ? manipulator(event.target.value)
                    : event.target.value;
                  onChange({
                    ...event,
                    target: {
                      ...event.target,
                      value: manipulatedValue,
                    },
                  });
                }}
              />
            );
          }}
        />
      </StyleWrapper>
    </Box>
  );
};
